import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { DocumentMapLocations } from '../../../../../core/models/freight-document/DocumentMapLocations';
import { TranslocoPipe } from '@ngneat/transloco';
import { ReportedLocation } from '../../../../../core/models/freight-document/LocationReport';
import { DatetimePipe } from '../../../../../shared/pipes/datetime.pipe';
import L, { LatLng, latLng, tileLayer } from 'leaflet';

@Component({
  selector: 'app-reported-location-map',
  template: `
    <div id="map" style="width: 100%; height: 70vh;"></div>`,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportedLocationMapComponent implements OnInit {
  @Input() locations: DocumentMapLocations;

  map: L.Map;

  constructor(private translocoPipe: TranslocoPipe, private datetimePipe: DatetimePipe) {
  }

  ngOnInit(): void {
    this.initMap();
    this.manageRoute();
    this.manageViewport();
  }

  private initMap(): void {
    const configuration = {
      layers: [
        tileLayer(`https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png`, {
          minZoom: 5,
          maxZoom: 18,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          referrerPolicy: 'origin-when-cross-origin',
        }),
      ],
      zoom: 13,
      center: latLng(48.137154, 11.576124),
    };
    this.map = L.map('map', configuration);
  }

  private manageRoute(): void {
    if (!this.locations) {
      return;
    }

    this.createMarker(this.locations.getCollectionLocation(), 'mission-collection');
    this.createMarker(this.locations.getDeliveryLocation(), 'mission-delivery');
    this.locations.getCarrierToCarrierLocations().forEach((reportedLocation) =>
      this.createMarker(reportedLocation, 'mission-carrier-to-carrier')
    );
    this.locations.getReportedLocations().forEach(
      (reportedLocation) => this.createMarker(reportedLocation, 'vehicle-point')
    );


    const gpsPoints = this.locations.getRoute().map((location) => L.latLng(location.lat, location.lng));
    const routeLine = L.polyline(gpsPoints, {
      color: '#000',
      weight: 4,
      opacity: 0.9,
    }).addTo(this.map);

    this.map.fitBounds(routeLine.getBounds(), { padding: [60, 60] });
  }

  private manageViewport(): void {
    const bounds: LatLng[] = [];
    this.locations
      .getAll()
      .filter(this.areValidCoordinates)
      .forEach((reportedLocation) => bounds.push(L.latLng(reportedLocation.lat, reportedLocation.lng)));
    this.map.fitBounds(L.latLngBounds(bounds), { duration: 0, padding: [80, 80] });
  }

  createMarker(reportedLocation: ReportedLocation, iconName: string): void {
    if (!this.areValidCoordinates(reportedLocation)) {
      return;
    }

    const customDivIcon = L.divIcon({
      className: `icon icon-${iconName}-circle`,
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    });

    const marker = L.marker([reportedLocation.lat, reportedLocation.lng], { icon: customDivIcon });
    marker.bindPopup(this.getPopupContent(reportedLocation));
    marker.addTo(this.map);
  }

  areValidCoordinates(reportedLocation: ReportedLocation): boolean {
    return !(Number.isNaN(reportedLocation?.lng) || Number.isNaN(reportedLocation?.lat));
  }

  private getPopupContent(reportedLocation: ReportedLocation): string {
    const driver = `<div class="col-12">
                      <span class="label">${this.translocoPipe.transform('transportOperations.fds.map.info.driver')}:</span>
                      <span class="font-weight-bold">${reportedLocation?.info?.driverName ? reportedLocation.info.driverName : ''}</span>
                    </div>`;
    const time = `<div class="col-12">
                    <span class="label">${this.translocoPipe.transform('transportOperations.fds.map.info.submittedAt')}:</span>
                    <span class="font-weight-bold">${this.datetimePipe.transform(reportedLocation.timestamp)}</span>
                  </div>`;
    return `<div class="p-3">
                    <div class="row">
                        ${reportedLocation.info?.driverName ? driver : ''}
                        ${time}
                    </div>
                  </div>`;
  }
}
